"use client";

import { Loader2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect } from "react";
import CardFullProvider from "~/app/(private)/words/CardFullProvider";
import WordGridContainer from "~/app/(private)/words/WordGridContainer";
import { useIsMobile } from "~/hooks/use-mobile";
import { useInfiniteScroll } from "~/hooks/useInfiniteScroll";
import { type SelectTranscriptWithLexemes } from "~/server/db/transcripts/schema";
import { useShowKnownLemmas } from "~/state/videos/hooks/use-show-known-lemmas";

export default function TranscriptList({
  translationId,
  transcripts,
  hasMore,
  ListItemComponent,
  jlptLevel,
  reContain = true,
}: {
  translationId: number;
  transcripts: SelectTranscriptWithLexemes[];
  hasMore: boolean;
  jlptLevel?: number;
  ListItemComponent: React.ElementType;
  reContain?: boolean;
}) {
  const searchParams = useSearchParams();
  const { isShowKnownLemmasEnabled } = useShowKnownLemmas();

  const isMobile = useIsMobile();
  const filterUniqueLexemes = !!jlptLevel;

  const cursorParam = searchParams.get("cursor");

  const {
    data: allTranscripts,
    ref,
    hasMore: stillHasMore,
    loadFromCursor,
  } = useInfiniteScroll({
    initialData: transcripts,
    hasMore,
    paginationType: "cursor",
    getCursor: (lastTranscript) => lastTranscript.start,
    onLoadMore: async ({ cursor }) => {
      const res = await fetch(
        `/api/transcripts/${translationId}?cursor=${cursor}&jlptLevel=${jlptLevel ?? 0}`,
      );
      const { data, hasMore } = (await res.json()) as {
        data: SelectTranscriptWithLexemes[];
        hasMore: boolean;
      };
      return { data, hasMore };
    },
    filterData: filterUniqueLexemes
      ? (data) => {
          const seenLexemes = new Set<number>();
          return data.map((transcript) => ({
            ...transcript,
            lexemes: transcript.lexemes.filter((lexeme) => {
              if (seenLexemes.has(lexeme.lexeme.id)) return false;
              seenLexemes.add(lexeme.lexeme.id);
              return true;
            }),
          }));
        }
      : undefined,
  });

  useEffect(() => {
    if (cursorParam) void loadFromCursor(cursorParam);
  }, [cursorParam]);

  const Container = reContain ? WordGridContainer : "div";

  return (
    <Container>
      <CardFullProvider>
        {({ onShowFullCard }) => (
          <>
            {allTranscripts.map((transcript) => (
              <ListItemComponent
                key={transcript.id}
                isMobile={isMobile}
                transcript={transcript}
                showKnownLemmas={isShowKnownLemmasEnabled}
                onShowFullCard={onShowFullCard}
              />
            ))}
            {stillHasMore && (
              <div
                ref={ref}
                className="col-span-full flex h-4 justify-center pb-24 opacity-50"
              >
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
          </>
        )}
      </CardFullProvider>
    </Container>
  );
}
